import { VideoStreamRenderer } from "@azure/communication-calling";
import mute_incall from '../new_assets/in_call_mic_off.svg';
import unmute_incall from '../new_assets/in_call_mic_on.svg';
import raise_hand_active from "../new_assets/raise_hand.svg";
import { appendRoleLabel, getParticipantRoleLabel } from "./participantRoleUtils";

export const displayLocalVideoStream = async ({
  localVideoStream,
  localVideoContainerRef,
  displayName,
  userId,
  isAudioOngoingForLocal,
  participantCount,
  isScreenSharing = false,
  localParticipant = null,
}) => {
  if (!localVideoStream) {
    console.error("No local video stream to display");
    return;
  }

  // Check if the container reference is available
  if (!localVideoContainerRef || !localVideoContainerRef.current) {
    console.error("Local video container reference is not available");
    return;
  }

  try {
    console.log("Displaying local video stream...");
    const renderer = new VideoStreamRenderer(localVideoStream);
    const view = await renderer.createView();
    view.target.style.position = "relative";
    view.target.style.zIndex = "1";

    // Clear the container first
    localVideoContainerRef.current.innerHTML = "";

    const localContainer = document.createElement("div");
    localContainer.style.width = "100%";
    localContainer.style.height = "100%";
    localContainer.style.borderRadius = "10px";
    localContainer.style.overflow = "hidden";
    localContainer.appendChild(view.target);
    
    // Set hidden to false and append the container
    localVideoContainerRef.current.hidden = false;
    localVideoContainerRef.current.appendChild(localContainer);

    const shortNameElement = document.createElement("div");
    shortNameElement.className = "short-name";
    const baseLabel = displayName ? `You (${displayName})` : 'You';
    const roleLabel = getParticipantRoleLabel(localParticipant);
    shortNameElement.innerText = appendRoleLabel(baseLabel, roleLabel);
    localContainer.appendChild(shortNameElement);

    const NameAbbrElement = document.createElement("div");
    NameAbbrElement.className = "name_div";
    NameAbbrElement.innerText = getInitials(displayName);
    localContainer.appendChild(NameAbbrElement);

    const muteStatusElement = document.createElement("img");
    // Use different CSS classes based on participant count and screen sharing status
    if (isScreenSharing) {
      muteStatusElement.className = "mute-status-screen-share";
    } else {
      muteStatusElement.className = participantCount < 3 ? 'mute-status' : 'mute-status';
    }
    muteStatusElement.src = isAudioOngoingForLocal ? unmute_incall : mute_incall;
    muteStatusElement.id = `mute-status-${userId}`;
    // Set background color based on mute status
    muteStatusElement.style.backgroundColor = isAudioOngoingForLocal ? '#16A249' : '#FF1D1D';
    muteStatusElement.style.borderRadius = '50%';
    muteStatusElement.style.padding = '4px';
    if (isAudioOngoingForLocal) muteStatusElement.classList.add('mute');
    localContainer.appendChild(muteStatusElement);

    const risehandStatusElement = document.createElement("img");
    risehandStatusElement.className = "risehand-status";
    risehandStatusElement.src = raise_hand_active;
    risehandStatusElement.style.display = "none";
    risehandStatusElement.id = `risehand-status-${userId}`;
    localContainer.appendChild(risehandStatusElement);

    // Check for pending raise hand updates and apply border
    if (window.pendingRaiseHandUpdates && window.pendingRaiseHandUpdates[userId]) {
      const pendingValue = window.pendingRaiseHandUpdates[userId];
      risehandStatusElement.style.display = pendingValue ? 'block' : 'none';

      // Apply border if hand is raised
      if (pendingValue) {
        localContainer.style.border = '2px solid #F25920';
        localContainer.style.boxShadow = '0 0 0 2px #F25920';
      }

      delete window.pendingRaiseHandUpdates[userId];
    }
  } catch (error) {
    console.error("Error displaying local video stream:", error);
  }
};

export const getInitials = (str) => {
  try {
    // Handle null, undefined, or non-string values
    if (!str || typeof str !== 'string') {
      return '';
    }
    
    let initials = "";
    if (!str.includes(" ")) {
      initials = str.slice(0, 1).toUpperCase();
    } else {
      const words = str.split(" ");
      initials = words.map((word) => word.charAt(0).toUpperCase()).join("");
      if (initials.length > 2) {
        initials = initials.slice(0, 1);
      }
    }
    return initials;
  } catch (error) {
    console.error("Error generating initials:", error);
    return '';
  }
};

export const removeLocalVideoStream = ({
  localVideoContainerRef,
  participantCount,
  participantCountGlobal,
  isRightPanelOpen,
  isAudioOngoingForLocal,
  userId,
  displayName,
  localParticipant = null,
} = {}) => {
  // Check if the container reference is available
  if (!localVideoContainerRef || !localVideoContainerRef.current) {
    console.error("Local video container reference is not available for removal");
    return;
  }

  try {
    localVideoContainerRef.current.innerHTML = "";

    const shortNameContainer = document.createElement("div");
    shortNameContainer.className = getClassName(participantCountGlobal, isRightPanelOpen);
    localVideoContainerRef.current.appendChild(shortNameContainer);

    const shortNameElement = document.createElement("div");
    shortNameElement.className = "short-name";
    const baseLabel = displayName ? `You (${displayName})` : 'You';
    const roleLabel = getParticipantRoleLabel(localParticipant);
    shortNameElement.innerText = appendRoleLabel(baseLabel, roleLabel);
    shortNameContainer.appendChild(shortNameElement);

    const NameAbbrElement = document.createElement("div");
    NameAbbrElement.className = "name_div";
    NameAbbrElement.innerText = getInitials(displayName);
    shortNameContainer.appendChild(NameAbbrElement);

    const muteStatusElement = document.createElement("img");
    muteStatusElement.className = "mute-status";
    muteStatusElement.src = isAudioOngoingForLocal ? unmute_incall : mute_incall;
    muteStatusElement.id = `mute-status-${userId}`;
    // Set background color based on mute status
    muteStatusElement.style.backgroundColor = isAudioOngoingForLocal ? '#16A249' : '#FF1D1D';
    muteStatusElement.style.borderRadius = '50%';
    muteStatusElement.style.padding = '4px';
    if (isAudioOngoingForLocal) muteStatusElement.classList.add('mute');
    shortNameContainer.appendChild(muteStatusElement);

    const risehandStatusElement = document.createElement("img");
    risehandStatusElement.className = "risehand-status";
    risehandStatusElement.src = raise_hand_active;
    risehandStatusElement.style.display = "none";
    risehandStatusElement.id = `risehand-status-${userId}`;
    shortNameContainer.appendChild(risehandStatusElement);

    // Check for pending raise hand updates and apply border
    if (window.pendingRaiseHandUpdates && window.pendingRaiseHandUpdates[userId]) {
      const pendingValue = window.pendingRaiseHandUpdates[userId];
      risehandStatusElement.style.display = pendingValue ? 'block' : 'none';

      // Apply border if hand is raised
      if (pendingValue) {
        shortNameContainer.style.border = '2px solid #F25920';
        shortNameContainer.style.boxShadow = '0 0 0 2px #F25920';
      }

      delete window.pendingRaiseHandUpdates[userId];
    }

    localVideoContainerRef.current.hidden = false;
  } catch (error) {
    console.error("Error in removeLocalVideoStream:", error);
  }
};

const getClassName = (participantCountGlobal, isRightPanelOpen, pinnedParticipantId) => {
  if (!pinnedParticipantId && isRightPanelOpen && participantCountGlobal < 3) {
    return "local-video-container-custom";
  }
  return participantCountGlobal < 3 && !isRightPanelOpen
    ? "local-video-container-custom"
    : participantCountGlobal < 3 && isRightPanelOpen
      ? "local-video-container-withmenu-custom"
      : participantCountGlobal === 3
        ? "local-video-three-custom"
        : participantCountGlobal === 4
          ? "local-video-four-custom"
          : participantCountGlobal < 7
            ? "local-video-morethan-four-custom"
            : "local-video-morethan-six-custom";
};
